import { Title } from '@patternfly/react-core'
import {
  TableComposable,
  Tbody,
  InnerScrollContainer,
  OuterScrollContainer,
  Th,
  Td,
  Tr,
} from '@patternfly/react-table'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { EditFormModal } from '@app/components/form/edit-form'
import { convertDerivedValueToDisplay } from '@app/domain/derived-values/derived-values.helpers'
import { getOrderedQuestions } from '@app/domain/form/form.helpers'
import { useInstrument } from '@app/domain/instrument/instrument.hooks'
import type { IEntry } from '@app/domain/instrument/instrument.interface'

import { EmptyResponseHistoryRow } from './empty-response-history-row'
import { ResponseHistoryHeader } from './response-history-header'
import { ResponseHistoryRow } from './response-history-row'

interface Props {
  entries: IEntry[]
  isEditable?: boolean
  hideHeader?: boolean
  instrumentId: string
  instrumentVersion: number
  onEditResponses?: (entryId: string, responses: Record<string, string>) => void
}

export const ResponseHistoryTable = ({
  entries,
  isEditable,
  hideHeader,
  instrumentId,
  instrumentVersion,
  onEditResponses,
}: Props) => {
  const { instrument } = useInstrument(instrumentId, instrumentVersion)

  const [selectedEntry, setSelectedEntry] = useState<IEntry>(undefined)
  const [isEditInstrumentModalOpen, setEditInstrumentModalOpen] = useState(
    false,
  )

  const handleOpenEditInstrumentModal = useCallback((entry: IEntry) => {
    setSelectedEntry(entry)
    setEditInstrumentModalOpen(true)
  }, [])

  const onCloseEditInstrumentModal = useCallback(() => {
    setEditInstrumentModalOpen(false)
  }, [])

  const onSubmitEditInstrument = useCallback(
    (entry: IEntry, responses: Record<string, string>) => {
      setEditInstrumentModalOpen(false)
      onEditResponses?.(entry.id, responses)
    },
    [onEditResponses],
  )

  const responseRows = useMemo(() => {
    const getResponses = (key: string) => {
      const responses = []
      entries.forEach((entry) => responses.push(entry.responses[key]))
      return responses
    }

    const questionResponses = []
    const questions = instrument ? getOrderedQuestions(instrument) : []

    questions.forEach((question) => {
      questionResponses.push(getResponses(question.id))
    })

    if (questions.length > 0) {
      return questions.map((question, idx) => (
        <ResponseHistoryRow
          key={question.id}
          question={question}
          responses={questionResponses[idx]}
        />
      ))
    } else {
      return (
        <EmptyResponseHistoryRow
          key={1}
          content="This instrument does not have any questions configured."
          numberOfEntries={entries.length}
        />
      )
    }
  }, [entries, instrument])

  const derivedValueRows = useMemo(() => {
    const getDerivedValue = (id: string) => {
      const derivedValues = []
      entries.forEach((entry) => derivedValues.push(entry.derivedValues[id]))
      return derivedValues
    }

    const derivedValueIds = entries.reduce(
      (result: Set<string>, entry: IEntry) => {
        const idsInEntry = new Set<string>(Object.keys(entry.derivedValues))
        return new Set([...result, ...idsInEntry])
      },
      new Set<string>(),
    )

    if (derivedValueIds.size > 0) {
      return Array.from(derivedValueIds.values()).map((id) => (
        <Tr key={id}>
          <Th isStickyColumn modifier="wrap" width={10}>
            <span>{id}</span>
          </Th>
          {getDerivedValue(id).map((value, idx) => (
            <Td width={10} modifier="wrap" key={idx}>
              {convertDerivedValueToDisplay(value)}
            </Td>
          ))}
        </Tr>
      ))
    } else {
      return []
    }
  }, [entries])

  return (
    <>
      {selectedEntry ? (
        <EditFormModal
          key={selectedEntry.id}
          instrument={instrument}
          entry={selectedEntry}
          isModalOpen={isEditInstrumentModalOpen}
          onCancel={onCloseEditInstrumentModal}
          onSubmit={onSubmitEditInstrument}
        />
      ) : null}
      <div style={{ height: '600px' }}>
        <OuterScrollContainer>
          <InnerScrollContainer>
            <Title headingLevel="h1">Responses</Title>
            <TableComposable
              aria-label="Sticky columns and header table"
              gridBreakPoint=""
              isStickyHeader
              isStriped
              borders={false}
              style={{ tableLayout: 'auto' }}
            >
              {!hideHeader ? (
                <ResponseHistoryHeader
                  entries={entries}
                  instrument={instrument}
                  editResponses={isEditable}
                  openEditInstrumentModal={handleOpenEditInstrumentModal}
                />
              ) : null}
              <Tbody>{responseRows}</Tbody>
            </TableComposable>

            {derivedValueRows.length > 0 ? (
              <>
                <Title headingLevel="h1">Derived Values</Title>
                <TableComposable
                  aria-label="Sticky columns and header table"
                  gridBreakPoint=""
                  isStickyHeader
                  isStriped
                  borders={false}
                  style={{ tableLayout: 'auto' }}
                >
                  {!hideHeader ? (
                    <ResponseHistoryHeader
                      entries={entries}
                      instrument={instrument}
                      editResponses={false}
                    />
                  ) : null}
                  <Tbody>{derivedValueRows}</Tbody>
                </TableComposable>
              </>
            ) : null}
          </InnerScrollContainer>
        </OuterScrollContainer>
      </div>
    </>
  )
}
